import { FC, ReactNode, useCallback } from "react"
import { ReleaseCycle } from "~/ui-rtk/api/types"
import { SidebarItem } from "./SidebarItem"
import { Divider } from "~/ui-rtk/components/ui/common"
import { SetupInfo } from "./components/SetupInfo"
import {
  ConnectorsSetupLabel,
  KeywordsSetupLabel,
} from "./components/CustomLabels"

import {
  sidebarItems as sidebarItemsFactory,
  setupSidebarItems,
  sosFreeSidebarItems,
  TSetupSidebarItem,
  ALL_ROLES,
} from "./constants"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { ChartBarIcon } from "../../ui/common/Icons/ChartBarIcon"
import {
  TAnimatedSvgIconProps,
  TAnimatedSvgIconSize,
} from "../../ui/common/Icons/IconType"
import { ForwardRefComponent } from "motion/react"
import { useGetCompanyCustomReportsQuery } from "~/ui-rtk/api/companyCustomReportApi"
import { useGetCompanyArtifactsQuery } from "~/ui-rtk/api/companyArtifactApi"
import { SparklesIcon } from "../../ui/common/Icons/SparklesIcon"
import useAccess from "~/ui-rtk/hooks/access"

import useShopifyAccess from "~/ui-rtk/hooks/shopify-access"
import useGAConnectorConnected from "~/ui-rtk/hooks/ga-connector-connected"
import useMediaMentionsAccess from "~/ui-rtk/hooks/media-mentions-access"

import { useConnect } from "./connect"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useCreativeReportsPageAccess from "~/ui-rtk/hooks/creative-reports-access"
import useMentionsReviewAccess from "~/ui-rtk/hooks/mentions-review-access"

import useHowAISeesYourBrandAccess from "~/ui-rtk/hooks/how-ai-sees-your-brand-access"
import usePaidMediaWeightsAccess from "~/ui-rtk/hooks/paid-media-weights-access"
import useBudgetManagementAccess from "~/ui-rtk/hooks/budget-management-access"
import useExperimentsAccess from "~/ui-rtk/hooks/experiment-access"
import useWebMerchandisingAccess from "~/ui-rtk/hooks/web-merchandising-access"

const Sidebar: FC = () => {
  const {
    showSidebarProducts,
    isSosFree,
    isExpanded,
    sessionRole,
    expand,
    collapse,
    handleBeforeNavigate,
    handleNavigate,
    setupDetails,
    isSetupDetailsLoading,
  } = useConnect()

  const sidebarItems = sidebarItemsFactory({
    isGAConnectorConnected: useGAConnectorConnected(),
    isShopifyEnabled: useShopifyAccess(),
    isMediaMentionsEnabled:
      useMediaMentionsAccess() || useMentionsReviewAccess(),
    isCreativeReportsEnabled: useCreativeReportsPageAccess(),
    isBrandPositioningEnabled: useHowAISeesYourBrandAccess(),
    isPaidMediaWeightsEnabled: usePaidMediaWeightsAccess(),
    isBudgetManagementEnabled: useBudgetManagementAccess(),
    isExperimentsEnabled: useExperimentsAccess(),
    isWebMerchandisingEnabled: useWebMerchandisingAccess(),
  })

  // Custom reports are dynamic per-company records; each becomes a sub-nav item
  // (oldest first, matching the API order), plus an "+ Add Custom Report" link.
  const { data: customReports } = useGetCompanyCustomReportsQuery()
  const customReportSubItems = [
    ...(customReports ?? []).map(report => ({
      label: report.name,
      path: brandRoutes.CUSTOM_REPORTS.VIEW.replace(":id", report.id),
      availableTo: ALL_ROLES,
    })),
    {
      label: "+ Add Custom Report",
      path: brandRoutes.CUSTOM_REPORTS.NEW,
      availableTo: ALL_ROLES,
    },
  ]

  // Agency-published artifacts: listed for everyone once one exists; the
  // agency (super admins) always sees the entry so it can publish the first.
  const { isSuperAdmin } = useAccess()
  const { data: artifacts } = useGetCompanyArtifactsQuery()
  const showArtifacts = isSuperAdmin || Boolean(artifacts?.length)
  const artifactSubItems = [
    ...(artifacts ?? []).map(artifact => ({
      label: artifact.title,
      path: brandRoutes.ARTIFACTS.VIEW.replace(":id", artifact.id),
      availableTo: ALL_ROLES,
    })),
    {
      label: isSuperAdmin ? "All artifacts / publish" : "All artifacts",
      path: brandRoutes.ARTIFACTS.ROOT,
      availableTo: ALL_ROLES,
    },
  ]

  const renderSetupItem = useCallback(
    (setupItem: TSetupSidebarItem) => {
      const subItems = setupItem?.items?.map(item => {
        const isSetupItem =
          brandRoutes.SETUP.KEYWORDS.includes(item.path) ||
          brandRoutes.SETUP.CONNECTORS.includes(item.path)

        if (!isSetupItem) {
          return item
        }

        let label: ReactNode | string = item.label
        if (item.path === brandRoutes.SETUP.CONNECTORS) {
          label = (
            <ConnectorsSetupLabel
              item={item}
              isLoading={isSetupDetailsLoading}
              setupDetails={setupDetails}
            />
          )
        } else if (item.path === brandRoutes.SETUP.KEYWORDS) {
          label = (
            <KeywordsSetupLabel
              item={item}
              isLoading={isSetupDetailsLoading}
              setupDetails={setupDetails}
            />
          )
        }

        return { ...item, label }
      })

      return (
        <SidebarItem
          key={setupItem.path}
          onBeforeNavigate={handleBeforeNavigate}
          onNavigate={handleNavigate}
          labelVisible={isExpanded}
          sessionRole={sessionRole}
          {...setupItem}
          items={subItems}
        />
      )
    },
    [isExpanded, sessionRole, setupDetails],
  )

  const renderInfoMessage = useCallback(() => {
    const { essentialUnconnectedConnectors, hasMissingKeywords } = setupDetails

    // Filter out ALPHA connectors to match the same logic as other components
    const filteredUnconnectedConnectors = essentialUnconnectedConnectors.filter(
      connector => connector.releaseCycle !== ReleaseCycle.ALPHA,
    )

    if (!filteredUnconnectedConnectors.length || !hasMissingKeywords) {
      return null
    }

    return (
      <SetupInfo isExpanded={isExpanded} collapse={collapse}>
        <SetupInfo.Message connectors={filteredUnconnectedConnectors} />
      </SetupInfo>
    )
  }, [setupDetails, isExpanded])
  // trigger

  return (
    <div
      onMouseEnter={expand}
      onMouseLeave={collapse}
      className={cn(
        "fixed flex flex-col justify-between w-16 pb-20 top-16 left-0 z-[1000] h-screen overflow-y-auto transition-all bg-basic-black border-r border-r-basic-blue",
        isExpanded && "w-70",
      )}
    >
      <ul className="flex flex-col w-full gap-3 px-2 m-0">
        {isSosFree ? (
          // Free SoS companies: minimal nav only (APP-493) — no product
          // upsell items, no setup section, no connector setup info.
          sosFreeSidebarItems.map(item => (
            <SidebarItem
              onBeforeNavigate={handleBeforeNavigate}
              onNavigate={handleNavigate}
              key={item.path}
              labelVisible={isExpanded}
              sessionRole={sessionRole}
              {...item}
            />
          ))
        ) : (
          <>
            {showSidebarProducts && (
              <>
                {sidebarItems.map(item => (
                  <SidebarItem
                    onBeforeNavigate={handleBeforeNavigate}
                    onNavigate={handleNavigate}
                    key={item.path}
                    labelVisible={isExpanded}
                    sessionRole={sessionRole}
                    {...item}
                  />
                ))}
                <SidebarItem
                  onBeforeNavigate={handleBeforeNavigate}
                  onNavigate={handleNavigate}
                  key="custom-reports"
                  labelVisible={isExpanded}
                  sessionRole={sessionRole}
                  label="Custom Reports"
                  path={brandRoutes.CUSTOM_REPORTS.NEW}
                  Icon={
                    ChartBarIcon as ForwardRefComponent<
                      TAnimatedSvgIconProps,
                      TAnimatedSvgIconSize
                    >
                  }
                  availableTo={ALL_ROLES}
                  items={customReportSubItems}
                />
                {showArtifacts && (
                  <SidebarItem
                    onBeforeNavigate={handleBeforeNavigate}
                    onNavigate={handleNavigate}
                    key="artifacts"
                    labelVisible={isExpanded}
                    sessionRole={sessionRole}
                    label="Artifacts"
                    path={brandRoutes.ARTIFACTS.ROOT}
                    Icon={
                      SparklesIcon as ForwardRefComponent<
                        TAnimatedSvgIconProps,
                        TAnimatedSvgIconSize
                      >
                    }
                    availableTo={ALL_ROLES}
                    items={artifactSubItems}
                  />
                )}
                <Divider />
              </>
            )}
            {setupSidebarItems.map(renderSetupItem)}
          </>
        )}
      </ul>
      {!isSosFree && renderInfoMessage()}
    </div>
  )
}

export default Sidebar
